import { PrizeTier } from '../types';

/**
 * Utility functions for sharing quiz results
 */

interface ShareData {
  correctAnswers: number;
  totalQuestions: number;
  prize?: PrizeTier;
  cashedOut: boolean;
  walkedOut: boolean;
}

/**
 * Get the app base URL from environment or fallback
 * Best practice: Use REACT_APP_REDIRECT_URI and extract the base URL
 */
export const getAppBaseUrl = (): string => {
  const redirectUri = process.env.REACT_APP_REDIRECT_URI;
  
  if (redirectUri) {
    try {
      const url = new URL(redirectUri);
      return `${url.protocol}//${url.host}`;
    } catch (e) {
      console.warn('Failed to parse REACT_APP_REDIRECT_URI, using fallback');
    }
  }
  
  // Fallback to current origin
  return window.location.origin;
};

/**
 * Generate a shareable text message about the quiz result
 */
export const generateShareMessage = (data: ShareData): string => {
  const { correctAnswers, totalQuestions, prize, cashedOut, walkedOut } = data;
  
  let message = `🎯 I just played the Shipment CoCaptain Quiz!\n\n`;
  
  if (walkedOut) {
    message += `🚪 I walked out after ${correctAnswers} correct answer${correctAnswers !== 1 ? 's' : ''}.\n`;
    message += `Better luck next time! 💪\n`;
  } else if (correctAnswers === totalQuestions) {
    message += `🏆 PERFECT SCORE! ${correctAnswers}/${totalQuestions} correct!\n`;
    if (prize) {
      message += `🎁 Prize Won: ${prize.label}\n`;
    }
    message += `\nI'm a true CoCaptain champion! 🎉\n`;
  } else if (correctAnswers >= totalQuestions * 0.7) {
    message += `🎉 Great performance! ${correctAnswers}/${totalQuestions} correct!\n`;
    if (prize) {
      message += `${cashedOut ? '💰 Cashed out' : '🎁 Won'}: ${prize.label}\n`;
    }
  } else {
    message += `👍 Scored ${correctAnswers}/${totalQuestions}\n`;
    if (prize) {
      message += `${cashedOut ? '💰 Cashed out' : '🎁 Won'}: ${prize.label}\n`;
    }
  }
  
  message += `\nThink you can beat my score? Try it yourself! 🚢\n`;
  
  return message;
};

/**
 * Generate a LinkedIn-formatted share message
 * Includes professional tone and mandatory hashtag
 */
export const generateLinkedInMessage = (data: ShareData): string => {
  const { correctAnswers, totalQuestions, prize, cashedOut, walkedOut } = data;
  
  let message = `🚢 Just tested my GenAI and supply chain knowledge with the Shipment CoCaptain Quiz!\n\n`;
  
  if (walkedOut) {
    message += `📊 Result: ${correctAnswers}/${totalQuestions} correct answers (walked out)\n`;
    message += `\nIt's a challenging quiz that really tests your understanding of AI-powered supply chain solutions. Looking forward to improving my score!\n`;
  } else if (correctAnswers === totalQuestions) {
    message += `🏆 Achievement Unlocked: PERFECT SCORE!\n`;
    message += `📊 Result: ${correctAnswers}/${totalQuestions} correct answers\n`;
    if (prize) {
      message += `🎁 Prize Tier: ${prize.label}\n`;
    }
    message += `\nProud to demonstrate deep knowledge of GenAI applications in supply chain management! 🎯\n`;
  } else if (correctAnswers >= totalQuestions * 0.7) {
    message += `✨ Strong Performance!\n`;
    message += `📊 Result: ${correctAnswers}/${totalQuestions} correct answers\n`;
    if (prize) {
      message += `🎁 ${cashedOut ? 'Cashed out at' : 'Achieved'}: ${prize.label}\n`;
    }
    message += `\nGreat experience learning more about AI-powered logistics and Shipment CoCaptain's capabilities!\n`;
  } else {
    message += `📊 Result: ${correctAnswers}/${totalQuestions} correct answers\n`;
    if (prize) {
      message += `🎁 ${cashedOut ? 'Cashed out at' : 'Achieved'}: ${prize.label}\n`;
    }
    message += `\nInteresting quiz that highlights the innovative use of GenAI in supply chain operations!\n`;
  }
  
  message += `\n💡 Want to test your knowledge of GenAI in supply chain? Take the challenge!\n`;
  message += `\n#ShipmentCoCaptainTDD #SupplyChain #GenAI #Innovation #Maersk #AI #Logistics`;
  
  return message;
};

/**
 * Generate a shareable URL with encoded quiz result data
 */
export const generateShareUrl = (data: ShareData): string => {
  const baseUrl = getAppBaseUrl();
  const params = new URLSearchParams({
    score: data.correctAnswers.toString(),
    total: data.totalQuestions.toString(),
    ...(data.prize && { prize: data.prize.label }),
    ...(data.cashedOut && { cashedOut: 'true' }),
    ...(data.walkedOut && { walkedOut: 'true' }),
  });
  
  return `${baseUrl}?shared=true&${params.toString()}`;
};

/**
 * Generate LinkedIn share URL
 * Opens LinkedIn share dialog with pre-filled content
 */
export const generateLinkedInShareUrl = (data: ShareData): string => {
  const shareUrl = generateShareUrl(data);
  // Note: LinkedIn's sharing API doesn't support pre-filled text via URL parameters
  // The message is stored for potential use with LinkedIn's JavaScript SDK
  // For now, we just pass the URL and users will need to add their own commentary
  
  // LinkedIn share URL format
  const linkedInUrl = new URL('https://www.linkedin.com/sharing/share-offsite/');
  linkedInUrl.searchParams.set('url', shareUrl);
  
  return linkedInUrl.toString();
};

/**
 * Copy share link to clipboard
 */
export const copyShareLinkToClipboard = async (data: ShareData): Promise<boolean> => {
  const shareUrl = generateShareUrl(data);
  const message = generateShareMessage(data);
  const fullText = `${message}\n${shareUrl}`;
  
  try {
    await navigator.clipboard.writeText(fullText);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = fullText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    } catch (fallbackError) {
      console.error('Fallback copy failed:', fallbackError);
      return false;
    }
  }
};

/**
 * Open LinkedIn share dialog
 * Note: For better LinkedIn integration, consider using LinkedIn's JavaScript SDK
 */
export const openLinkedInShare = (data: ShareData): void => {
  const linkedInUrl = generateLinkedInShareUrl(data);
  
  // Open in a popup window
  const width = 600;
  const height = 600;
  const left = (window.screen.width - width) / 2;
  const top = (window.screen.height - height) / 2;
  
  window.open(
    linkedInUrl,
    'linkedin-share',
    `width=${width},height=${height},left=${left},top=${top},toolbar=0,menubar=0,location=0,status=0,scrollbars=1,resizable=1`
  );
};

/**
 * Generate QR code data URL (placeholder for future implementation)
 * Recommend using a library like 'qrcode' for actual implementation
 */
export const generateQRCodeDataUrl = async (data: ShareData): Promise<string> => {
  // TODO: Implement QR code generation
  // Recommended: npm install qrcode @types/qrcode
  // import QRCode from 'qrcode';
  // const shareUrl = generateShareUrl(data);
  // return await QRCode.toDataURL(shareUrl);
  
  return ''; // Placeholder
};
